import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';

const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50];

export default function Pagination({ currentPage, totalItems, itemsPerPage, onPageChange, onItemsPerPageChange }) {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (totalItems <= 10) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-4" data-testid="pagination">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span>{totalItems} résultat{totalItems > 1 ? 's' : ''}</span>
        <span className="text-gray-300">|</span>
        <select
          data-testid="pagination-per-page"
          value={itemsPerPage}
          onChange={(e) => {
            onItemsPerPageChange(Number(e.target.value));
            onPageChange(1);
          }}
          className="text-sm border border-gray-200 rounded-md px-2 py-1 bg-white"
        >
          {ITEMS_PER_PAGE_OPTIONS.map(n => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          className="h-8 w-8 p-0"
          data-testid="pagination-prev"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {getPageNumbers().map((page) => (
          <Button
            key={page}
            variant={page === currentPage ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onPageChange(page)}
            className={`h-8 w-8 p-0 text-xs ${page === currentPage ? 'bg-[#0f1d3c] text-white' : ''}`}
            data-testid={`pagination-page-${page}`}
          >
            {page}
          </Button>
        ))}

        <Button
          variant="ghost"
          size="sm"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className="h-8 w-8 p-0"
          data-testid="pagination-next"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export function usePagination(initialPerPage = 20) {
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(initialPerPage);

  const paginate = (items) => {
    const start = (page - 1) * perPage;
    return items.slice(start, start + perPage);
  };

  const reset = () => setPage(1);

  return { page, setPage, perPage, setPerPage, paginate, reset };
}
